import { permissionEngine } from '@/core/permissions/permissionEngine';
import { useAuth } from '@/core/auth/AuthContext';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { StandardListLayout } from '@/shared/layout/StandardListLayout';
import { Filter, Download, Plus, Minus, Upload, FolderTree, ChevronDown, MoreHorizontal } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Input } from '@/components/ui';
import { Dropdown } from '@/components/ui/Dropdown';
import { InventoryKpiGrid } from './components/InventoryKpiGrid';
import { InventoryMobileCards } from './components/InventoryMobileCards';
import { InventoryTable } from './components/InventoryTable';

import { InventoryToolbar } from './components/InventoryToolbar';
import { useInventoryList } from './hooks/useInventoryList';
import { inventoryExcelService } from './services/inventoryExcel.service';
import type { InventoryItem } from './types/inventoryList.types';

export function InventoryListPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.items.view');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);

  const {
    data,
    filters,
    filterOptions,
    isLoading,
    updateSearch,
    updateStatus,
    updateFilter,
    updatePage,
    updatePageSize,
    deleteItem,
    isDeleting,
    canDelete,
  } = useInventoryList();
  const [selectedItem, setSelectedItem] = useState<InventoryItem | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<InventoryItem | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card' | 'compact'>('table');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    setSelectedItem((current) => {
      if (current && data.items.some((item) => item.id === current.id)) return current;
      return data.items[0];
    });
  }, [data.items]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteItem(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleExport = () => {
    inventoryExcelService.exportToExcel(data.items);
    toast.success('Xuất dữ liệu ra Excel thành công');
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.company_id) return;
    
    setIsImporting(true);
    try {
      const count = await inventoryExcelService.importFromExcel(file, profile.company_id);
      toast.success(`Nhập thành công ${count} vật tư`);
      queryClient.invalidateQueries({ queryKey: ['inventory', 'list'] });
    } catch (error: any) {
      toast.error('Lỗi khi nhập Excel: ' + error.message);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <StandardListLayout
      title="Kho vật tư"
      description="Quản lý toàn bộ vật tư dùng chung cho thi công và bảo dưỡng."
      isLoading={isLoading}
      viewMode={viewMode}
      actions={
        <div className="hidden">
          {/* File Input is kept but hidden for programmatic trigger */}
          <input
            type="file"
            accept=".xlsx, .xls, .csv"
            className="hidden"
            ref={fileInputRef}
            onChange={handleImport}
          />
        </div>
      }
      kpiSection={<InventoryKpiGrid stats={data.stats} isLoading={isLoading} />}
      toolbarSection={
        <InventoryToolbar
          search={filters.search}
          onSearchChange={updateSearch}
          status={filters.status}
          onStatusChange={updateStatus}
          filters={filters}
          onFilterChange={updateFilter}
          filterOptions={filterOptions}
          onExport={handleExport}
          onAdd={() => navigate('/inventory/items/new')}
        />
      }
      tableSection={
        <InventoryTable
          items={data.items}
          total={data.total}
          page={filters.page}
          pageSize={filters.pageSize}
          isLoading={isLoading}
          selectedId={selectedItem?.id}
          onSelect={setSelectedItem}
          onView={(id) => navigate(`/inventory/items/detail/${id}`)}
          onEdit={(id) => navigate(`/inventory/items/edit/${id}`)}
          onDelete={setDeleteTarget}
          canDelete={canDelete}
          onPageChange={updatePage}
          onPageSizeChange={updatePageSize}
        />
      }
      cardSection={
        <InventoryMobileCards
          items={data.items}
          total={data.total}
          page={filters.page}
          pageSize={filters.pageSize}
          isLoading={isLoading}
          onSelect={setSelectedItem}
          onView={(id) => navigate(`/inventory/items/detail/${id}`)}
          onEdit={(id) => navigate(`/inventory/items/edit/${id}`)}
          onDelete={setDeleteTarget}
          canDelete={canDelete}
          onPageChange={updatePage}
          onPageSizeChange={updatePageSize}
        />
      }
    >
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xóa vật tư"
        message={
          <span>
            Bạn có chắc chắn muốn xóa vật tư <strong>{deleteTarget?.name}</strong>? Hành động này không thể hoàn tác.
          </span>
        }
        confirmText="Xóa"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </StandardListLayout>
  );
}

export default InventoryListPage;

